import { useCallback, useEffect } from 'react';
import {
  signalSurveyCompleted,
  signalReady,
  signalSurveyHeightChanged,
  signalSurveyPageChanged
} from './messageHandler';

interface UseSignalsArgs {
  enablePopup?: boolean;
  instanceId?: string | null;
  getBackgroundColor?: () => string;
  isLegacySurvey?: boolean;
}

const useSignals = ({
  enablePopup,
  instanceId,
  getBackgroundColor,
  isLegacySurvey = false
}: UseSignalsArgs) => {
  // Setup resize observer for popup survey - use method for ref, to ensure that the observer is attached when the ref is populated
  const attachSurveyContentRef = useCallback(
    (node: HTMLElement | null) => {
      if (node && enablePopup) {
        const observer = new ResizeObserver((entries) => {
          for (const entry of entries) {
            const newHeight = entry.borderBoxSize[0].blockSize;
            signalSurveyHeightChanged(instanceId, newHeight);
          }
        });
        observer.observe(node);

        // Return cleanup function
        return () => {
          observer.disconnect();
        };
      }
      return undefined;
    },
    [enablePopup, instanceId]
  );

  // Setup resize observer for legacy survey
  useEffect(() => {
    if (!isLegacySurvey) {
      return undefined;
    }
    const targetElement = document.getElementById('survey-body');
    if (!targetElement) {
      return undefined;
    }
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newHeight = entry.borderBoxSize[0].blockSize;
        signalSurveyHeightChanged(instanceId, newHeight);
      }
    });
    observer.observe(targetElement);
    return () => {
      observer.disconnect();
    };
  }, [isLegacySurvey, enablePopup, instanceId]);

  const signalSurveyCompletedCallback = useCallback(() => {
    return signalSurveyCompleted(instanceId);
  }, [instanceId]);

  const signalReadyCallback = useCallback(() => {
    return signalReady(instanceId, getBackgroundColor?.());
  }, [instanceId, getBackgroundColor]);

  const signalSurveyPageChangedCallback = useCallback(() => {
    return signalSurveyPageChanged(instanceId, getBackgroundColor?.());
  }, [instanceId, getBackgroundColor]);

  return {
    signalSurveyCompleted: signalSurveyCompletedCallback,
    signalReady: signalReadyCallback,
    signalSurveyPageChanged: signalSurveyPageChangedCallback,
    attachSurveyContentRef
  };
};

export default useSignals;
